/**
 * @element qds-pagination
 *
 * Page navigation and page-size control for qds-data-table, usable standalone
 * under any paged list.
 *
 * @attr {number} page - 1-based current page (default 1, clamped to [1, pageCount])
 * @attr {number} page-size - Items per page (default 25)
 * @attr {number} total - Total items, not pages (default 0)
 * @attr {string} page-size-options - Comma-separated page sizes (default "10,25,50,100")
 * @attr {number} sibling-count - Page buttons either side of current (default 1)
 * @attr {number} boundary-count - Page buttons pinned at each end (default 1)
 * @attr {boolean} show-first-last - Show first/last buttons (default true)
 * @attr {boolean} show-page-size - Show page-size selector (default true)
 * @attr {boolean} show-summary - Show range summary (default true)
 * @attr {boolean} compact - Compact mode: prev/next and "Page N of M" only (default false)
 * @attr {boolean} disabled - Disable all controls (default false)
 * @attr {string} size - Control size: sm|md|lg (default md)
 *
 * @property {number[]} pageSizeOptions - Array of page size options
 * @property {number} pageCount - Read-only total page count (Math.max(1, Math.ceil(total / pageSize)))
 *
 * @csspart container - The nav container
 * @csspart summary - The range summary span
 * @csspart page-size - The page-size selector wrapper
 * @csspart page-size-label - The "Rows:" label
 * @csspart controls - The page controls wrapper
 * @csspart first - The first-page button
 * @csspart prev - The previous-page button
 * @csspart page - A page-number button
 * @csspart page-current - The current-page button (also carries the `page` part)
 * @csspart ellipsis - An ellipsis span
 * @csspart next - The next-page button
 * @csspart last - The last-page button
 * @csspart compact-label - The compact "Page N of M" label
 *
 * @fires qds-page-change - When page or page size changes via user interaction.
 *   detail: { component, page, pageSize, total }
 *
 * @example
 * <qds-pagination page="1" page-size="25" total="1284"></qds-pagination>
 */

import { loadBaseCSS, adoptPrimeIconsStyles } from '../shared/button-utils.js';
import { isAttrTruthy, parseSize, upgradeProperty } from '../shared/form-control.js';
import type { QdsPageChangeEventDetail, QdsControlSize } from '../../types/index';

type PageItem =
  | { type: 'page'; page: number }
  | { type: 'ellipsis'; side: 'left' | 'right' };

export class QdsPagination extends HTMLElement {
  private _page = 1;
  private _pageSize = 25;
  private _total = 0;
  private _pageSizeOptions: number[] = [10, 25, 50, 100];
  private _siblingCount = 1;
  private _boundaryCount = 1;
  private _showFirstLast = true;
  private _showPageSize = true;
  private _showSummary = true;
  private _compact = false;
  private _disabled = false;
  private _size: QdsControlSize = 'md';
  private _cleanup: (() => void) | null = null;

  static get observedAttributes(): string[] {
    return [
      'page',
      'page-size',
      'total',
      'page-size-options',
      'sibling-count',
      'boundary-count',
      'show-first-last',
      'show-page-size',
      'show-summary',
      'compact',
      'disabled',
      'size',
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    loadBaseCSS();
  }

  // ── Properties ────────────────────────────────────────────────────────

  get page(): number {
    return this._page;
  }
  set page(value: number) {
    const clamped = this._clampPage(value);
    this._page = clamped;
    this.setAttribute('page', String(clamped));
  }

  get pageSize(): number {
    return this._pageSize;
  }
  set pageSize(value: number) {
    this._pageSize = Math.max(1, value);
    this.setAttribute('page-size', String(this._pageSize));
  }

  get total(): number {
    return this._total;
  }
  set total(value: number) {
    this._total = Math.max(0, value);
    this.setAttribute('total', String(this._total));
  }

  get pageSizeOptions(): number[] {
    return [...this._pageSizeOptions];
  }
  set pageSizeOptions(value: number[]) {
    if (Array.isArray(value)) {
      this._pageSizeOptions = value
        .filter((v) => v > 0)
        .sort((a, b) => a - b);
      this.setAttribute(
        'page-size-options',
        this._pageSizeOptions.join(','),
      );
    }
  }

  get siblingCount(): number {
    return this._siblingCount;
  }
  set siblingCount(value: number) {
    this._siblingCount = Math.max(0, value);
    this.setAttribute('sibling-count', String(this._siblingCount));
  }

  get boundaryCount(): number {
    return this._boundaryCount;
  }
  set boundaryCount(value: number) {
    this._boundaryCount = Math.max(0, value);
    this.setAttribute('boundary-count', String(this._boundaryCount));
  }

  get showFirstLast(): boolean {
    return this._showFirstLast;
  }
  set showFirstLast(value: boolean) {
    this._showFirstLast = value;
    if (value) this.setAttribute('show-first-last', '');
    else this.removeAttribute('show-first-last');
  }

  get showPageSize(): boolean {
    return this._showPageSize;
  }
  set showPageSize(value: boolean) {
    this._showPageSize = value;
    if (value) this.setAttribute('show-page-size', '');
    else this.removeAttribute('show-page-size');
  }

  get showSummary(): boolean {
    return this._showSummary;
  }
  set showSummary(value: boolean) {
    this._showSummary = value;
    if (value) this.setAttribute('show-summary', '');
    else this.removeAttribute('show-summary');
  }

  get compact(): boolean {
    return this._compact;
  }
  set compact(value: boolean) {
    this._compact = value;
    if (value) this.setAttribute('compact', '');
    else this.removeAttribute('compact');
  }

  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = value;
    if (value) this.setAttribute('disabled', '');
    else this.removeAttribute('disabled');
  }

  get size(): QdsControlSize {
    return this._size;
  }
  set size(value: QdsControlSize) {
    this._size = value;
    this.setAttribute('size', value);
  }

  /** Read-only total page count. */
  get pageCount(): number {
    return Math.max(1, Math.ceil(this._total / this._pageSize));
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────

  connectedCallback(): void {
    if (!this.isConnected) return;
    upgradeProperty(this, 'pageSizeOptions');
    this.render();
  }

  disconnectedCallback(): void {
    this._cleanupListeners();
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'page': {
        const parsed = parseInt(newValue ?? '1', 10);
        const clamped = this._clampPage(isNaN(parsed) ? 1 : parsed);
        this._page = clamped;
        // Reflect clamped value back if it differs from the attribute
        if (String(clamped) !== newValue) {
          this.setAttribute('page', String(clamped));
          return; // attributeChangedCallback will re-fire and render
        }
        break;
      }
      case 'page-size': {
        const parsed = parseInt(newValue ?? '25', 10);
        this._pageSize = Math.max(1, isNaN(parsed) ? 25 : parsed);
        this._page = this._clampPage(this._page);
        break;
      }
      case 'total': {
        const parsed = parseInt(newValue ?? '0', 10);
        this._total = Math.max(0, isNaN(parsed) ? 0 : parsed);
        this._page = this._clampPage(this._page);
        break;
      }
      case 'page-size-options':
        this._pageSizeOptions = this._parsePageSizeOptions(newValue);
        break;
      case 'sibling-count': {
        const parsed = parseInt(newValue ?? '1', 10);
        this._siblingCount = Math.max(0, isNaN(parsed) ? 1 : parsed);
        break;
      }
      case 'boundary-count': {
        const parsed = parseInt(newValue ?? '1', 10);
        this._boundaryCount = Math.max(0, isNaN(parsed) ? 1 : parsed);
        break;
      }
      case 'show-first-last':
        this._showFirstLast = isAttrTruthy(newValue);
        break;
      case 'show-page-size':
        this._showPageSize = isAttrTruthy(newValue);
        break;
      case 'show-summary':
        this._showSummary = isAttrTruthy(newValue);
        break;
      case 'compact':
        this._compact = isAttrTruthy(newValue);
        break;
      case 'disabled':
        this._disabled = isAttrTruthy(newValue);
        break;
      case 'size':
        this._size = parseSize(newValue);
        break;
      default:
        return;
    }

    if (this.isConnected) this.render();
  }

  // ── Private helpers ───────────────────────────────────────────────────

  private _clampPage(value: number): number {
    return Math.min(Math.max(1, value), this.pageCount);
  }

  private _parsePageSizeOptions(value: string | null): number[] {
    if (!value || !value.trim()) return [10, 25, 50, 100];
    const parts = value
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n > 0);
    return parts.length > 0
      ? parts.sort((a, b) => a - b)
      : [10, 25, 50, 100];
  }

  private _formatNumber(n: number): string {
    return new Intl.NumberFormat().format(n);
  }

  private _getRangeSummary(): string {
    if (this._total === 0) return '0 of 0';
    const start = (this._page - 1) * this._pageSize + 1;
    const end = Math.min(this._page * this._pageSize, this._total);
    return `${this._formatNumber(start)}–${this._formatNumber(end)} of ${this._formatNumber(this._total)}`;
  }

  private _getCompactLabel(): string {
    return `Page ${this._formatNumber(this._page)} of ${this._formatNumber(this.pageCount)}`;
  }

  /**
   * Compute the page items to render.
   *
   * With sibling-count=1, boundary-count=1, page 26 of 52 renders:
   *   1 ... 25 26 27 ... 52
   *
   * Near the start the window extends toward the middle so the control keeps
   * a stable width: page 1 of 52 renders 1 2 3 ... 52, not 1 ... 52.
   *
   * Below 2*(sibling+boundary)+3 total pages, every page is rendered with no
   * ellipsis.
   */
  private _computePageItems(): PageItem[] {
    const pc = this.pageCount;
    const sib = this._siblingCount;
    const bnd = this._boundaryCount;
    const threshold = 2 * (sib + bnd) + 3;

    if (pc <= threshold) {
      const items: PageItem[] = [];
      for (let i = 1; i <= pc; i++) items.push({ type: 'page', page: i });
      return items;
    }

    // Center window: page +/- sibling, clamped to valid range
    let centerStart = Math.max(this._page - sib, 1);
    let centerEnd = Math.min(this._page + sib, pc);

    // Extend toward the middle when near a boundary to keep stable width
    const leftDeficit = sib - (this._page - centerStart);
    const rightDeficit = sib - (centerEnd - this._page);
    if (leftDeficit > 0 && rightDeficit === 0) {
      centerEnd = Math.min(centerEnd + leftDeficit, pc);
    }
    if (rightDeficit > 0 && leftDeficit === 0) {
      centerStart = Math.max(centerStart - rightDeficit, 1);
    }

    const leftBoundaryEnd = bnd;
    const rightBoundaryStart = pc - bnd + 1;

    const items: PageItem[] = [];

    // Left section: boundary pages + ellipsis or fill, then center
    if (centerStart > leftBoundaryEnd + 1) {
      // Gap between left boundary and center
      for (let i = 1; i <= bnd; i++) {
        items.push({ type: 'page', page: i });
      }
      if (centerStart > leftBoundaryEnd + 2) {
        items.push({ type: 'ellipsis', side: 'left' });
      } else {
        // Only 1 page hidden — show it instead of an ellipsis
        items.push({ type: 'page', page: leftBoundaryEnd + 1 });
      }
      for (let i = centerStart; i <= centerEnd; i++) {
        items.push({ type: 'page', page: i });
      }
    } else {
      // No gap — render from page 1 through centerEnd
      for (let i = 1; i <= centerEnd; i++) {
        items.push({ type: 'page', page: i });
      }
    }

    // Right section: ellipsis or fill, then boundary pages
    if (centerEnd < rightBoundaryStart - 1) {
      // Gap between center and right boundary
      if (centerEnd < rightBoundaryStart - 2) {
        items.push({ type: 'ellipsis', side: 'right' });
      } else {
        // Only 1 page hidden — show it
        items.push({ type: 'page', page: rightBoundaryStart - 1 });
      }
      for (let i = rightBoundaryStart; i <= pc; i++) {
        items.push({ type: 'page', page: i });
      }
    } else if (centerEnd < pc) {
      // Center is adjacent to or overlaps the right boundary
      for (let i = centerEnd + 1; i <= pc; i++) {
        items.push({ type: 'page', page: i });
      }
    }

    return items;
  }

  // ── Event dispatch ────────────────────────────────────────────────────

  private _dispatchPageChange(): void {
    const detail: QdsPageChangeEventDetail = {
      component: this,
      page: this._page,
      pageSize: this._pageSize,
      total: this._total,
    };
    this.dispatchEvent(
      new CustomEvent('qds-page-change', {
        bubbles: true,
        composed: true,
        detail,
      }),
    );
  }

  // ── Event handlers (bound via arrow-function properties) ──────────────

  private _handlePageClick = (e: Event): void => {
    const btn = e.currentTarget as HTMLButtonElement;
    const page = parseInt(btn.dataset.page ?? '1', 10);
    if (page === this._page) return; // no event for current page
    this._page = page;
    this.setAttribute('page', String(page));
    this._dispatchPageChange();
  };

  private _handleFirst = (): void => {
    if (this._page === 1) return;
    this._page = 1;
    this.setAttribute('page', '1');
    this._dispatchPageChange();
  };

  private _handlePrev = (): void => {
    if (this._page <= 1) return;
    this._page = this._page - 1;
    this.setAttribute('page', String(this._page));
    this._dispatchPageChange();
  };

  private _handleNext = (): void => {
    if (this._page >= this.pageCount) return;
    this._page = this._page + 1;
    this.setAttribute('page', String(this._page));
    this._dispatchPageChange();
  };

  private _handleLast = (): void => {
    if (this._page >= this.pageCount) return;
    this._page = this.pageCount;
    this.setAttribute('page', String(this._page));
    this._dispatchPageChange();
  };

  private _handlePageSizeChange = (e: Event): void => {
    const select = e.currentTarget as HTMLSelectElement;
    const newSize = parseInt(select.value, 10);
    if (isNaN(newSize) || newSize === this._pageSize) return;

    // Preserve the first visible item's page, not the page number
    const firstVisibleItem = (this._page - 1) * this._pageSize + 1;
    this._pageSize = newSize;
    const newPageCount = Math.max(1, Math.ceil(this._total / newSize));
    this._page = Math.min(
      Math.max(1, Math.ceil(firstVisibleItem / newSize)),
      newPageCount,
    );

    this.setAttribute('page-size', String(newSize));
    this.setAttribute('page', String(this._page));
    this._dispatchPageChange();
  };

  private _handlePageKeydown = (e: KeyboardEvent): void => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const root = this.shadowRoot;
    if (!root) return;
    const buttons = Array.from(
      root.querySelectorAll<HTMLButtonElement>('button[data-page]'),
    );
    if (buttons.length === 0) return;
    const focused = root.activeElement as HTMLButtonElement | null;
    const currentIndex = buttons.findIndex((b) => b === focused);
    if (currentIndex === -1) return;
    const nextIndex =
      e.key === 'ArrowLeft' ? currentIndex - 1 : currentIndex + 1;
    if (nextIndex >= 0 && nextIndex < buttons.length) {
      buttons[nextIndex].focus();
    }
  };

  // ── Listener management ───────────────────────────────────────────────

  private _cleanupListeners(): void {
    if (this._cleanup) {
      this._cleanup();
      this._cleanup = null;
    }
  }

  private _attachListeners(): void {
    this._cleanupListeners();
    const root = this.shadowRoot;
    if (!root) return;

    const entries: Array<{
      el: EventTarget;
      type: string;
      fn: EventListenerOrEventListenerObject;
    }> = [];
    const add = (
      el: EventTarget | null,
      type: string,
      fn: EventListenerOrEventListenerObject,
    ): void => {
      if (el) {
        el.addEventListener(type, fn);
        entries.push({ el, type, fn });
      }
    };

    // Page buttons
    root.querySelectorAll<HTMLButtonElement>('button[data-page]').forEach(
      (btn) => add(btn, 'click', this._handlePageClick as EventListener),
    );

    // Nav buttons
    add(root.querySelector('[part~="first"]'), 'click', this._handleFirst as EventListener);
    add(root.querySelector('[part~="prev"]'), 'click', this._handlePrev as EventListener);
    add(root.querySelector('[part~="next"]'), 'click', this._handleNext as EventListener);
    add(root.querySelector('[part~="last"]'), 'click', this._handleLast as EventListener);

    // Page-size select
    add(root.querySelector('select'), 'change', this._handlePageSizeChange as EventListener);

    // Keyboard navigation for page buttons group
    add(
      root.querySelector('.page-buttons'),
      'keydown',
      this._handlePageKeydown as EventListener,
    );

    this._cleanup = () => {
      entries.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
    };
  }

  // ── Render ────────────────────────────────────────────────────────────

  private render(): void {
    const root = this.shadowRoot;
    if (!root) return;

    const pc = this.pageCount;
    const onFirst = this._page <= 1;
    const onLast = this._page >= pc;
    const allDisabled = this._disabled || pc <= 1;

    // Build page-size options HTML
    const pageSizeOptionsHtml = this._pageSizeOptions
      .map(
        (opt) =>
          `<option value="${opt}"${opt === this._pageSize ? ' selected' : ''}>${this._formatNumber(opt)}</option>`,
      )
      .join('');

    // Build page items
    let pageItemsHtml = '';
    if (!this._compact) {
      const items = this._computePageItems();
      pageItemsHtml = items
        .map((item) => {
          if (item.type === 'ellipsis') {
            return `<span class="ellipsis" part="ellipsis" aria-hidden="true">…</span>`;
          }
          const isCurrent = item.page === this._page;
          return `<button class="page-btn${isCurrent ? ' current' : ''}" part="page${isCurrent ? ' page-current' : ''}" data-page="${item.page}"${allDisabled ? ' disabled' : ''} aria-label="Go to page ${item.page}"${isCurrent ? ' aria-current="page"' : ''}>${item.page}</button>`;
        })
        .join('');
    }

    // Build sections
    const summaryHtml = this._showSummary
      ? `<span class="summary" part="summary" aria-live="polite">${this._getRangeSummary()}</span>`
      : '';

    const pageSizeHtml = this._showPageSize
      ? `<div class="page-size" part="page-size">
           <span class="page-size-label" part="page-size-label">Rows:</span>
           <select class="page-size-select" aria-label="Rows per page"${allDisabled ? ' disabled' : ''}>${pageSizeOptionsHtml}</select>
         </div>`
      : '';

    const compactLabelHtml = this._compact
      ? `<span class="compact-label" part="compact-label" aria-live="polite">${this._getCompactLabel()}</span>`
      : '';

    const firstBtnHtml = this._showFirstLast
      ? `<button class="nav-btn" part="first" aria-label="First page"${onFirst || allDisabled ? ' disabled' : ''}><i class="pi pi-angle-double-left"></i></button>`
      : '';

    const prevBtnHtml = `<button class="nav-btn" part="prev" aria-label="Previous page"${onFirst || allDisabled ? ' disabled' : ''}><i class="pi pi-angle-left"></i></button>`;

    const pageButtonsHtml = !this._compact
      ? `<div class="page-buttons" role="group" aria-label="Pagination">${pageItemsHtml}</div>`
      : '';

    const nextBtnHtml = `<button class="nav-btn" part="next" aria-label="Next page"${onLast || allDisabled ? ' disabled' : ''}><i class="pi pi-angle-right"></i></button>`;

    const lastBtnHtml = this._showFirstLast
      ? `<button class="nav-btn" part="last" aria-label="Last page"${onLast || allDisabled ? ' disabled' : ''}><i class="pi pi-angle-double-right"></i></button>`
      : '';

    root.innerHTML = `
      <style>${this._getStyles()}</style>
      <nav part="container" aria-label="Pagination">
        <div class="left-section">
          ${summaryHtml}
          ${pageSizeHtml}
        </div>
        <div class="controls" part="controls">
          ${compactLabelHtml}
          ${firstBtnHtml}
          ${prevBtnHtml}
          ${pageButtonsHtml}
          ${nextBtnHtml}
          ${lastBtnHtml}
        </div>
      </nav>
    `;

    void adoptPrimeIconsStyles(root);
    this._attachListeners();
  }

  private _getStyles(): string {
    return `
      :host {
        display: block;
        --qds-pagination-height: 48px;
        --qds-pagination-button-size: 32px;
        --qds-pagination-gap: var(--theme-space-1, 4px);
        --qds-pagination-border-color: var(--theme-border-primary, #E4E9F2);
      }

      nav {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: var(--qds-pagination-height);
        min-height: var(--qds-pagination-height);
        border-top: 1px solid var(--qds-pagination-border-color);
        padding: 0 var(--theme-space-3, 12px);
        box-sizing: border-box;
        font-family: var(--theme-font-family, inherit);
        font-size: var(--theme-font-size-sm, 14px);
        color: var(--theme-text-secondary, #555555);
      }

      .left-section {
        display: flex;
        align-items: center;
        gap: var(--theme-space-3, 12px);
        flex-wrap: wrap;
      }

      .summary {
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }

      .page-size {
        display: flex;
        align-items: center;
        gap: var(--theme-space-1, 4px);
      }

      .page-size-label {
        color: var(--theme-text-secondary, #555555);
        white-space: nowrap;
      }

      .page-size-select {
        height: var(--qds-control-height-sm, 32px);
        border: 1px solid var(--theme-border-primary, #E4E9F2);
        border-radius: var(--theme-radius-sm, 4px);
        background: var(--theme-background-primary, #FFFFFF);
        color: var(--theme-text-primary, #333333);
        padding: 0 var(--theme-space-2, 8px);
        font-size: var(--theme-font-size-sm, 14px);
        font-family: inherit;
        cursor: pointer;
        box-sizing: border-box;
        transition: border-color 0.15s, box-shadow 0.15s;
      }

      .page-size-select:hover:not(:disabled) {
        border-color: var(--theme-border-secondary, #AFBBD1);
      }

      .page-size-select:focus-visible {
        outline: none;
        border-color: var(--theme-accent-primary, #445E89);
        box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
      }

      .page-size-select:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      :host([size="md"]) .page-size-select {
        height: var(--qds-control-height-md, 40px);
      }
      :host([size="lg"]) .page-size-select {
        height: var(--qds-control-height-lg, 48px);
      }

      .controls {
        display: flex;
        align-items: center;
        gap: var(--qds-pagination-gap);
      }

      .page-buttons {
        display: flex;
        align-items: center;
        gap: var(--qds-pagination-gap);
      }

      .compact-label {
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
        color: var(--theme-text-primary, #333333);
        padding: 0 var(--qds-pagination-gap);
      }

      button {
        width: var(--qds-pagination-button-size);
        height: var(--qds-pagination-button-size);
        min-width: var(--qds-pagination-button-size);
        min-height: var(--qds-pagination-button-size);
        border: none;
        background: transparent;
        color: var(--theme-text-primary, #333333);
        border-radius: var(--theme-radius-sm, 4px);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        font-variant-numeric: tabular-nums;
        font-size: var(--theme-font-size-sm, 14px);
        font-family: inherit;
        transition: background-color 0.15s, color 0.15s, box-shadow 0.15s;
      }

      button:hover:not(:disabled) {
        background: var(--button-hover-bg, rgba(0, 0, 0, 0.05));
      }

      button:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px var(--button-focus-ring, rgba(68, 94, 137, 0.25));
      }

      button:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }

      button.current {
        background: var(--theme-accent-primary, #445E89);
        color: var(--theme-text-on-accent-primary, #FFFFFF);
      }

      button.current:hover:not(:disabled) {
        background: var(--theme-accent-primary, #445E89);
      }

      .nav-btn i {
        font-size: var(--icon-size, 20px);
        color: var(--icon-color-secondary, var(--theme-text-secondary, #555555));
      }

      .nav-btn:disabled i {
        color: var(--theme-text-tertiary, #6A6A6A);
      }

      .ellipsis {
        width: var(--qds-pagination-button-size);
        height: var(--qds-pagination-button-size);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--theme-text-tertiary, #6A6A6A);
        user-select: none;
        font-variant-numeric: tabular-nums;
      }

      :host([disabled]) {
        opacity: 0.55;
        pointer-events: none;
      }

      @media (pointer: coarse) {
        :host {
          --qds-pagination-button-size: 44px;
        }
        :host([size="sm"]) .page-size-select {
          height: max(var(--qds-control-height-sm, 32px), 44px);
        }
        :host([size="md"]) .page-size-select {
          height: max(var(--qds-control-height-md, 40px), 44px);
        }
      }
    `;
  }
}

if (!customElements.get('qds-pagination')) {
  customElements.define('qds-pagination', QdsPagination);
}
